import React from 'react'
import { createPortal } from 'react-dom'
import { Button, Heading, Text } from '../ui'
import type { SupportCenterAppointmentRecord } from '../../types/supportCenterReports'
import { APPOINTMENT_STATUS_LABELS } from '../../types/supportCenterReports'
import { TRIAGE_STATUS_LABELS } from '../../types/supportCenterTriage'
import { appointmentChannelLabel } from '../../types/supportCenterAppointments'
import { formatCpf, formatDateTime, formatPhone } from './dashboardHelpers'

const fieldClass =
  'w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 px-3 py-2 text-sm min-h-[2.5rem] whitespace-pre-wrap break-words'

function display(value: string | null | undefined): string {
  const text = value?.trim()
  return text ? text : '—'
}

function displayDate(value: string | null | undefined): string {
  if (!value?.trim()) return '—'
  return formatDateTime(value)
}

function joinLines(values: string[]): string {
  const items = values.map((value) => value.trim()).filter(Boolean)
  return items.length ? items.join('\n') : '—'
}

function personLine(name: string | null, cpf: string): string {
  const formatted = formatCpf(cpf)
  const label = name?.trim()
  if (label && formatted) return `${label}\n${formatted}`
  return label || formatted || '—'
}

function DetailField({
  label,
  value,
  wide,
}: {
  label: string
  value: React.ReactNode
  wide?: boolean
}) {
  return (
    <div className={wide ? 'md:col-span-2' : undefined}>
      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
        {label}
      </label>
      <div className={fieldClass}>{value || '—'}</div>
    </div>
  )
}

type AppointmentRecordModalProps = {
  record: SupportCenterAppointmentRecord
  onClose: () => void
}

export const AppointmentRecordModal: React.FC<AppointmentRecordModalProps> = ({
  record,
  onClose,
}) => {
  const statusLabel = APPOINTMENT_STATUS_LABELS[record.status] || record.status
  const coverage =
    record.assistance_name && record.assistance_code
      ? `${record.assistance_name} (${record.assistance_code})`
      : record.assistance_name || record.assistance_code

  return createPortal(
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 p-4"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose()
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="appointment-record-modal-title"
        className="bg-white dark:bg-gray-900 rounded-xl shadow-xl w-full max-w-4xl max-h-[92vh] flex flex-col"
        onMouseDown={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between gap-4 px-6 py-4 border-b border-gray-200 dark:border-gray-700 shrink-0">
          <div className="min-w-0">
            <Heading
              as="h2"
              size="2xl"
              id="appointment-record-modal-title"
              className="text-gray-900 dark:text-white"
            >
              Agendamento
            </Heading>
            <Text size="xs" color="muted" className="mt-0.5">
              {record.specialization_name || 'Especialidade'} · {statusLabel}
            </Text>
          </div>
          <Button variant="ghost" onClick={onClose}>
            Fechar
          </Button>
        </div>

        <div className="px-6 py-5 overflow-y-auto flex-1 min-h-0">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <DetailField label="Data agendada" value={displayDate(record.scheduled_at)} />
            <DetailField label="Status" value={display(statusLabel)} />
            <DetailField
              label="Canal de agendamento"
              value={appointmentChannelLabel(record.channel)}
            />
            <DetailField label="Segurado" value={personLine(record.holder_name, record.holder_cpf)} />
            <DetailField label="Paciente" value={personLine(record.patient_name, record.patient_cpf)} />
            <DetailField label="Parentesco" value={display(record.relationship_to_holder)} />
            <DetailField label="Especialidade" value={display(record.specialization_name)} />
            <DetailField label="Cobertura" value={display(coverage)} />
            <DetailField label="Profissional" value={display(record.professional_name)} />
            <DetailField
              label="Número da apólice"
              value={display(record.policy_code != null ? String(record.policy_code) : null)}
            />
            <DetailField
              label="Código do subgrupo"
              value={display(record.agreement_code != null ? String(record.agreement_code) : null)}
            />
            <DetailField
              label="Código do fornecedor"
              value={display(
                record.assistance_provider_code != null
                  ? String(record.assistance_provider_code)
                  : null
              )}
            />
            <DetailField label="E-mails internos" value={joinLines(record.internal_emails)} wide />
            <DetailField
              label="Telefone"
              value={display(formatPhone(record.patient_phone) || null)}
            />
            <DetailField label="E-mails de contato" value={joinLines(record.client_emails)} />
            <DetailField
              label="Link da chamada"
              value={
                record.call_link ? (
                  <a
                    href={record.call_link}
                    target="_blank"
                    rel="noreferrer"
                    className="text-primary-600 dark:text-primary-400 underline break-all"
                  >
                    {record.call_link}
                  </a>
                ) : (
                  '—'
                )
              }
              wide
            />
            <DetailField
              label="Link encurtado"
              value={
                record.call_link_short ? (
                  <a
                    href={record.call_link_short}
                    target="_blank"
                    rel="noreferrer"
                    className="text-primary-600 dark:text-primary-400 underline break-all"
                  >
                    {record.call_link_short}
                  </a>
                ) : (
                  '—'
                )
              }
              wide
            />
            <DetailField label="ID do evento Outlook" value={display(record.outlook_event_id)} wide />
            <DetailField
              label="Triagem"
              value={display(
                record.triage_status ? TRIAGE_STATUS_LABELS[record.triage_status] : null
              )}
            />
            <DetailField label="Confirmado em" value={displayDate(record.confirmed_at)} />
            <DetailField
              label="Confirmação solicitada em"
              value={displayDate(record.confirmation_requested_at)}
            />
            <DetailField
              label="Início do atendimento"
              value={displayDate(record.attendance_started_at)}
            />
            <DetailField
              label="Fim do atendimento"
              value={displayDate(record.attendance_completed_at)}
            />
            <DetailField
              label="Motivo do cancelamento"
              value={display(record.cancel_reason_name)}
              wide
            />
            <DetailField
              label="Canal do cancelamento"
              value={appointmentChannelLabel(record.cancellation_channel)}
            />
            <DetailField label="Excluído em" value={displayDate(record.deleted_at)} />
            <DetailField label="Excluído por" value={display(record.deleted_by_name)} />
            <DetailField label="Criado em" value={displayDate(record.created_at)} />
            <DetailField label="Atualizado em" value={displayDate(record.updated_at)} />
          </div>
        </div>

        <div className="flex justify-end gap-3 px-6 py-4 border-t border-gray-200 dark:border-gray-700 shrink-0">
          <Button variant="solid" onClick={onClose}>
            Fechar
          </Button>
        </div>
      </div>
    </div>,
    document.body
  )
}
